import React from 'react';
import Icon from '../../../components/AppIcon';

const MembershipOverview = ({ plans, subscriptions }) => {
  const getPlanDistribution = () => {
    const distribution = {};
    subscriptions?.forEach(sub => {
      const planName = sub?.membership_plans?.plan_name || 'Unknown';
      distribution[planName] = (distribution?.[planName] || 0) + 1;
    });
    return distribution;
  };

  const getStatusDistribution = () => {
    const distribution = {
      active: 0,
      paused: 0,
      cancelled: 0,
      expired: 0,
      pending: 0
    };
    subscriptions?.forEach(sub => {
      const status = sub?.status || 'pending';
      distribution[status] = (distribution?.[status] || 0) + 1;
    });
    return distribution;
  };

  const planDistribution = getPlanDistribution();
  const statusDistribution = getStatusDistribution();

  const getStatusColor = (status) => {
    const colors = {
      active: 'var(--color-success)',
      paused: 'var(--color-warning)',
      cancelled: 'var(--color-destructive)',
      expired: 'var(--color-muted-foreground)',
      pending: 'var(--color-primary)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'bg-success/10 text-success',
      paused: 'bg-warning/10 text-warning',
      cancelled: 'bg-destructive/10 text-destructive',
      expired: 'bg-muted text-muted-foreground',
      pending: 'bg-primary/10 text-primary'
    };
    return classes?.[status] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <h2 className="text-xl font-heading font-bold text-foreground mb-6 flex items-center gap-2">
        <Icon name="PieChart" size={24} color="var(--color-primary)" />
        Membership Overview
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
            Plan Distribution
          </h3>
          <div className="space-y-3">
            {plans?.map((plan) => {
              const count = planDistribution?.[plan?.plan_name] || 0;
              const percentage = subscriptions?.length > 0
                ? ((count / subscriptions?.length) * 100)?.toFixed(1)
                : 0;

              return (
                <div key={plan?.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      <span className="text-sm font-medium text-foreground">
                        {plan?.plan_name}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-foreground">{count}</span>
                      <span className="text-xs text-muted-foreground ml-1">({percentage}%)</span>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-full rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Distribution */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
            Subscription Status
          </h3>
          <div className="space-y-3">
            {Object.entries(statusDistribution)?.map(([status, count]) => {
              const percentage = subscriptions?.length > 0
                ? ((count / subscriptions?.length) * 100)?.toFixed(1)
                : 0;

              return (
                <div key={status} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getStatusColor(status) }}
                      ></div>
                      <span className="text-sm font-medium text-foreground capitalize">
                        {status}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-foreground">{count}</span>
                      <span className="text-xs text-muted-foreground ml-1">({percentage}%)</span>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: getStatusColor(status)
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{plans?.length || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Active Plans</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-success">{statusDistribution?.active || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Active Members</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-warning">{statusDistribution?.paused || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Paused</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-destructive">{statusDistribution?.cancelled || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Cancelled</p>
        </div>
      </div>
    </div>
  );
};

export default MembershipOverview;